import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';

import { WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateWorkflowEditor/constants';
import Accordion from 'dashboard/components/Accordion';
import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import FieldMappingsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldMappingsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import RunVerificationConfigMapping from 'dashboard/components/WorkflowControlPanel/StepConfigAction/RunVerificationConfigMapping';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import {
  filterUsableObjects,
  getTokensToDisplayValue,
  snakify,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { TemplateFilter } from 'dashboard/lib/workflows/objects';
import { ExtractionFieldType, ExtractionRequest } from 'dashboard/models/document';
import { BaseTrigger, BaseTriggerParams, Trigger } from 'dashboard/models/trigger';
import { Verification, VERIFICATION_TEMPLATE_FIELDS } from 'dashboard/models/verification';
import {
  AttributeComparisonCheckConfig,
  ComparisonInputsConfig,
  isAttributeComparisonCheck,
} from 'dashboard/models/verification-check';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import {
  VerificationTemplateVersion,
  VTMPLV_TEMPLATE_TYPE_MAP,
  VTMPLV_VERIFICATION_TYPE_MAP,
} from 'dashboard/models/verification-template-version';
import { WorkflowError, WorkflowFieldKey, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionRunVerificationGenericV1 } from 'dashboard/models/workflow-version';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

type SupportedVtmplvMap = {
  [K in VerificationTemplateVersion['type']]: {
    verificationTemplateType: VerificationTemplate['type'];
    verificationType: Verification['type'];
  };
};

const SUPPORTED_VTMPLV_LIST: VerificationTemplateVersion['type'][] = [
  ApiModel.VerificationTemplateVersionAamva,
  ApiModel.VerificationTemplateVersionBusinessWebsite,
  ApiModel.VerificationTemplateVersionDatabase,
  ApiModel.VerificationTemplateVersionDatabaseSerpro,
  ApiModel.VerificationTemplateVersionDatabaseStandard,
  ApiModel.VerificationTemplateVersionDatabaseBusiness,
  ApiModel.VerificationTemplateVersionDatabaseBusinessFootprint,
  ApiModel.VerificationTemplateVersionDatabasePhoneCarrier,
  ApiModel.VerificationTemplateVersionDatabaseTin,
  ApiModel.VerificationTemplateVersionDatabaseVatNumber,
  ApiModel.VerificationTemplateVersionDigitalIdSerpro,
  ApiModel.VerificationTemplateVersionDocument,
  ApiModel.VerificationTemplateVersionDatabaseEcbsv,
  ApiModel.VerificationTemplateVersionEmailAddress,
  ApiModel.VerificationTemplateVersionGovernmentId,
  ApiModel.VerificationTemplateVersionGovernmentIdNfc,
  ApiModel.VerificationTemplateVersionPhoneNumber,
  ApiModel.VerificationTemplateVersionSelfie,
];

const VERIFICATION_TYPES_WITH_GENERIC_COMPARISON_CHECKS = [
  ApiModel.VerificationDocument,
  ApiModel.VerificationDatabase,
  ApiModel.VerificationGovernmentId,
  ApiModel.VerificationSelfie,
];

interface ParamsRunVerificationGenericV1Props extends ParamsBaseProps {
  verificationTemplateVersions: {
    name: string;
    token: string;
    type: ApiModel;
    verificationTemplateId: string;
  }[];
  editable: boolean;
  errors: WorkflowError[];
  params: WorkflowActionRunVerificationGenericV1['params'];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  updateActionParams: (
    newParams: WorkflowActionRunVerificationGenericV1['params'],
    template?: TemplateFilter,
  ) => void;
}

const paramsForComparison = (comparisonInputsConfig: ComparisonInputsConfig, basePath: string) =>
  comparisonInputsConfig['match-requirements'].reduce<Record<string, string>>(
    (hsh, req) => ({
      ...hsh,
      ...(req.name ? { [req.name]: [basePath, req.name].join('.') } : {}),
    }),
    {},
  );

const paramsForExtraction = (extraction: ExtractionRequest, basePath: string) => {
  if (extraction.version === '2.0') {
    switch (extraction['field-type']) {
      case ExtractionFieldType.String:
        return {
          [extraction['field-name']]: [basePath, extraction['field-name']].join('.'),
        };
      case ExtractionFieldType.Address:
        return {
          ['address_street_1']: [basePath, 'address_street_1'].join('.'),
          ['address_street_2']: [basePath, 'address_street_2'].join('.'),
          ['address_city']: [basePath, 'address_city'].join('.'),
          ['address_subdivision']: [basePath, 'address_subdivision'].join('.'),
          ['address_postal_code']: [basePath, 'address_postal_code'].join('.'),
          ['address_country_code']: [basePath, 'address_country_code'].join('.'),
        };
    }
  } else {
    return extraction.subfields.reduce<Record<string, string>>(
      (hsh, subfield) => ({
        ...hsh,
        [subfield['source-field']]: [basePath, subfield['source-field']].join('.'),
      }),
      {},
    );
  }
};

const ParamsRunVerificationGenericV1: FC<ParamsRunVerificationGenericV1Props> = ({
  verificationTemplateVersions,
  trigger,
  usableObjects,
  step,
  editable,
  errors,
  params,
  updateStep,
  updateActionParams,
}) => {
  const SUPPORTED_VTMPLV_MAP: SupportedVtmplvMap = useMemo(() => {
    return Object.fromEntries(
      SUPPORTED_VTMPLV_LIST.map((type: VerificationTemplateVersion['type']) => [
        type,
        {
          verificationTemplateType: VTMPLV_TEMPLATE_TYPE_MAP[type],
          verificationType: VTMPLV_VERIFICATION_TYPE_MAP[type],
        },
      ]),
    ) as SupportedVtmplvMap;
  }, []);

  const hasVerificationTemplateView = usePermission(Permission.VerificationTemplateView);
  const isTemplateAvailable = useMemo(() => {
    if (params['verification-template-id']?.length > 0) {
      return (
        verificationTemplateVersions.findIndex(
          (verificationTemplateVersion) =>
            verificationTemplateVersion.verificationTemplateId ===
            params['verification-template-id'],
        ) >= 0
      );
    }
    return (
      !params['verification-template-version-id'] ||
      verificationTemplateVersions
        .map((vtv) => vtv.token)
        .includes(params['verification-template-version-id'])
    );
  }, [params, verificationTemplateVersions]);
  // Fetch and cache the full verification template version whenever a new verification template is selected
  const [verificationTemplateVersionIdToVersion, setIdToVerificationTemplateVersion] = useState<
    Record<string, VerificationTemplateVersion>
  >({});
  const fetchVerificationTemplateVersion = useCallback(
    async (verificationTemplateVersionId: string) => {
      if (verificationTemplateVersionId in verificationTemplateVersionIdToVersion) {
        return verificationTemplateVersionIdToVersion[verificationTemplateVersionId];
      }

      return SessionClientDashboard.getResource<VerificationTemplateVersion>(
        ApiEndpoint.VerificationTemplateVersion,
        verificationTemplateVersionId,
      ).then(({ data: fullVersion }) => {
        setIdToVerificationTemplateVersion((idToVersion) => ({
          ...idToVersion,
          [fullVersion.id]: fullVersion,
        }));
        return fullVersion;
      });
    },
    [verificationTemplateVersionIdToVersion],
  );

  const verificationTemplateVersionOptions = verificationTemplateVersions
    .filter(
      (verificationTemplateVersion) => verificationTemplateVersion.type in SUPPORTED_VTMPLV_MAP,
    )
    .map((verificationTemplateVersion) => ({
      label: verificationTemplateVersion.name,
      value: verificationTemplateVersion.token,
      type: verificationTemplateVersion.type,
      templateId: verificationTemplateVersion.verificationTemplateId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [showEditPanel, { disable: closeEditPanel, toggle: toggleEditPanel }] = useToggle(false);

  const objectOptions = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const forcedStatusOptions = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [WorkflowFieldObjectKey.Inquiry, WorkflowFieldObjectKey.Transaction],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  const allowedFieldPaths = useMemo(() => {
    return forcedStatusOptions.map((usableObject) => {
      const { value } = usableObject;
      const fieldPath = `${value}.${WorkflowFieldKey.Fields}`;
      return snakify(fieldPath);
    });
  }, [forcedStatusOptions]);

  const basePath = useMemo(() => {
    switch (trigger?.type) {
      case ApiModel.TriggerEvent:
      case ApiModel.TriggerSchedule:
      case ApiModel.TriggerRecurring:
      case ApiModel.TriggerDirect:
        return [BaseTrigger, WorkflowFieldKey.Fields].join('.');
      case ApiModel.TriggerApi:
        return [BaseTriggerParams, WorkflowFieldKey.Fields].join('.');
      case ApiModel.TriggerAccount:
      case ApiModel.TriggerCase:
        return [BaseTriggerParams].join('.');
      default:
        return null;
    }
  }, [trigger]);

  const getDefaultVerificationMeta = (verificationTemplateVersion: VerificationTemplateVersion) => {
    switch (verificationTemplateVersion.type) {
      case ApiModel.VerificationTemplateVersionDocument: {
        if (verificationTemplateVersion.attributes.extractions.length === 0 || basePath == null) {
          return null;
        }

        return {
          document: {
            'comparison-attributes': verificationTemplateVersion.attributes.extractions.reduce<
              Record<string, string>
            >(
              (hsh, extraction) => ({
                ...hsh,
                ...paramsForExtraction(extraction, basePath),
              }),
              {},
            ),
          },
        };
      }

      case ApiModel.VerificationTemplateVersionGovernmentId:
      case ApiModel.VerificationTemplateVersionDatabase: {
        const verificationType = verificationTemplateVersion.type.split('/')[1];
        const comparisonInputsCheckConfig = verificationTemplateVersion.attributes[
          'verification-checks'
        ]?.find((vcheck) => isAttributeComparisonCheck(vcheck)) as AttributeComparisonCheckConfig;
        const comparisonInputsConfig =
          comparisonInputsCheckConfig?.['comparison-inputs-config'] ?? null;
        if (comparisonInputsConfig == null || basePath == null) return null;

        return {
          [verificationType]: {
            'comparison-attributes': comparisonInputsConfig.reduce<Record<string, string>>(
              (hsh, config) => ({
                ...hsh,
                ...paramsForComparison(config, basePath),
              }),
              {},
            ),
          },
        };
      }
      case ApiModel.VerificationTemplateVersionSelfie: {
        return {
          selfie: {
            'comparison-attributes': { Selfies: [basePath, 'selfies'].join('.') },
          },
        };
      }
      default:
        return null;
    }
  };

  const updateVerificationMeta = (
    newVerificationMeta: WorkflowActionRunVerificationGenericV1['params']['verification-meta'],
  ) => {
    updateStep({
      ...step,
      action: {
        ...(step.action as WorkflowActionRunVerificationGenericV1),
        params: {
          ...params,
          'verification-meta': { ...params['verification-meta'], ...newVerificationMeta },
        },
      },
    });
  };

  const getComparisonAttributes = (verificationType: Verification['type']) => {
    switch (verificationType) {
      case ApiModel.VerificationDocument: {
        return params['verification-meta']?.['document']?.['comparison-attributes'];
      }
      case ApiModel.VerificationDatabase: {
        return params['verification-meta']?.['database']?.['comparison-attributes'];
      }
      case ApiModel.VerificationGovernmentId: {
        return params['verification-meta']?.['government-id']?.['comparison-attributes'];
      }
      case ApiModel.VerificationSelfie: {
        return params['verification-meta']?.['selfie']?.['comparison-attributes'];
      }
      default:
        return null;
    }
  };

  const handleVerificationTemplateVersionChange = async (value: string) => {
    const verificationTemplateVersion = verificationTemplateVersionOptions.find(
      (option) => option.value === value,
    );

    if (!verificationTemplateVersion) {
      return;
    }

    const fields =
      VERIFICATION_TEMPLATE_FIELDS[
        SUPPORTED_VTMPLV_MAP[
          verificationTemplateVersion.type as VerificationTemplateVersion['type']
        ].verificationTemplateType
      ];

    const fullVersion = await fetchVerificationTemplateVersion(verificationTemplateVersion.value);
    updateStep({
      ...step,
      name: `Run ${verificationTemplateVersion.label ?? 'Verification'}`,
      action: {
        ...(step.action as WorkflowActionRunVerificationGenericV1),
        params: {
          ...params,
          'verification-template-version-id': verificationTemplateVersion.value,
          'verification-template-id': fullVersion.relationships['verification-template']?.data?.id,
          'verification-fields': fields.reduce<Record<string, string>>((acc, field) => {
            const fieldKey =
              trigger?.type === ApiModel.TriggerDirect
                ? (WORKFLOW_OBJECT_FIELD_KEY_MAPPINGS[field] ?? snakify(field))
                : snakify(field);
            return {
              ...acc,
              [field]: basePath == null ? '' : [basePath, fieldKey].join('.'),
            };
          }, {}),
          'verification-type':
            SUPPORTED_VTMPLV_MAP[
              verificationTemplateVersion.type as VerificationTemplateVersion['type']
            ].verificationType,
          'verification-meta': getDefaultVerificationMeta(fullVersion),
        },
      },
    });
  };

  const handleCompleteOnTentativeChange = (value: boolean) => {
    updateActionParams({
      ...params,
      'complete-on-tentative': value,
    });
  };

  const handleObjectsChange = (value: string[]) => {
    updateActionParams({
      ...params,
      objects: value,
      object: value?.[0] ?? null,
    });
  };

  const handleEditFieldMappings = (value: Record<string, string>) => {
    updateActionParams({
      ...params,
      'verification-template-version-id': params['verification-template-version-id'],
      'verification-fields': value || {},
    });
  };

  const handleDebugForcedStatusChange = (value: string) => {
    updateActionParams({
      ...params,
      'debug-forced-status': value,
    });
  };

  const handleDebugForcedStatusCheckbox = ({
    target: { checked },
  }: ChangeEvent<HTMLInputElement>) => {
    updateActionParams({
      ...params,
      'debug-forced-status': checked ? '' : undefined,
    });
  };

  const showStatusOverrideFieldInput = params['debug-forced-status'] != null;

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Verification Template'
        id='params-run-verification-generic__template-verification-template-label'
        helpText={
          hasVerificationTemplateView
            ? isTemplateAvailable
              ? undefined
              : 'The current verification template is not available to you at this time.'
            : 'Your role does not have permissions to view verification templates.'
        }
      />
      <ReactSelect
        aria-labelledby='params-run-verification-generic__template-verification-template-label'
        value={
          hasVerificationTemplateView && isTemplateAvailable
            ? params['verification-template-version-id']
            : undefined
        }
        onChange={handleVerificationTemplateVersionChange}
        disabled={!editable || !hasVerificationTemplateView}
        placeholder={
          hasVerificationTemplateView
            ? isTemplateAvailable
              ? undefined
              : 'UNAVAILABLE'
            : 'RESTRICTED'
        }
        hasError={
          hasErrorFor('verification-template-version-id') ||
          hasErrorFor('verification-template-id') ||
          hasErrorFor('verification-type')
        }
        options={verificationTemplateVersionOptions.map((option) => ({
          label: `${option.label} - ${option.templateId}`,
          value: option.value,
        }))}
        menuPlacement='bottom'
      />
      {renderErrorMessageFor([
        'verification-template-id',
        'verification-template-version-id',
        'verification-type',
      ])}
      <Spacer height={2} />
      <Checkbox
        label={<p>Complete on tentative?</p>}
        disabled={!editable}
        id='params-run-verification-generic__complete-on-tentative-checkbox'
        checked={params['complete-on-tentative']}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          handleCompleteOnTentativeChange(e.target.checked);
        }}
      />
      {params['verification-type'] === ApiModel.VerificationDatabaseTin &&
        params['retry-window-seconds'] && (
          <>
            <Spacer height={2} />
            <ParamLabel
              labelText='Verification Retry Window'
              helpText='Please reach out to Persona support if you need to update this retry window.'
            />
            <Paragraph aria-label='Retry description'>
              {`In the event of a service outage, Persona will automatically retry the verification for ${params['retry-window-seconds'] / 3600} hours.`}
            </Paragraph>
          </>
        )}

      <Spacer height={2} />
      {isTemplateAvailable && (
        <Accordion defaultExpanded title='Verification Fields'>
          {renderErrorMessageFor('verification-fields')}
          <Spacer height={1} />
          {showEditPanel && (
            <FieldMappingsPanel
              title='Verification Fields'
              usableObjects={usableObjects}
              fieldMappings={params['verification-fields'] ?? {}}
              onOutsideClick={closeEditPanel}
              onUpdateFieldMappings={handleEditFieldMappings}
            />
          )}
          <FieldsList
            fields={params['verification-fields']}
            fieldKey='verification-fields'
            errors={errors}
            editable={editable && params['verification-template-version-id'] != null}
            placeholderEmptyText={
              params['verification-template-version-id'] != null
                ? 'No fields selected'
                : 'Please select a verification template'
            }
            toggleEditPanel={toggleEditPanel}
            tokensToDisplayValue={getTokensToDisplayValue(usableObjects)}
          />
        </Accordion>
      )}
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        {params['verification-type'] &&
          VERIFICATION_TYPES_WITH_GENERIC_COMPARISON_CHECKS.includes(params['verification-type']) &&
          getComparisonAttributes(params['verification-type']) && (
            <RunVerificationConfigMapping
              usableObjects={usableObjects}
              errors={errors}
              editable={editable}
              verificationMeta={params['verification-meta']}
              verificationType={params['verification-type']}
              updateVerificationMeta={updateVerificationMeta}
              getCurrentCompAttr={getComparisonAttributes}
            />
          )}
        <Spacer height={2} />
        <ParamLabel
          labelText='Sandbox Verification Status'
          htmlFor='params-run-verification-generic__debug_forced_status'
        />
        <Checkbox
          id='params-run-verification-generic__debug_forced_status'
          checked={showStatusOverrideFieldInput}
          onChange={handleDebugForcedStatusCheckbox}
          label='Override default status'
          disabled={!editable}
        />
        {showStatusOverrideFieldInput && (
          <>
            <Spacer height={2} />
            <ParamLabel
              labelText='Select field'
              htmlFor='params-run-verification-generic__status-field-input'
            />
            <FieldSchemaSelect
              id='params-run-verification-generic__status-field'
              initialValue={params['debug-forced-status'] || ''}
              usableObjects={forcedStatusOptions}
              allowedFieldPaths={allowedFieldPaths}
              onFieldSelect={(option) => handleDebugForcedStatusChange(option.value)}
              size='sm'
              hasError={hasErrorFor('input-fields')}
            />
            {renderErrorMessageFor('input-fields')}
          </>
        )}
        <Spacer height={2} />
        <ParamLabel
          labelText='Objects'
          htmlFor='params-run-verification-generic__objects-input'
          id='params-run-verification-generic__objects-input'
        />
        <ReactSelect
          aria-labelledby='params-run-verification-generic__objects-input'
          id='params-run-verification-generic__objects-input'
          onChange={handleObjectsChange}
          value={
            // Shim over multiple vs. single objects
            params.objects && params.objects.length > 0
              ? params.objects
              : [params.object].filter(Boolean)
          }
          options={objectOptions}
          disabled={!editable}
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          isMulti
        />
        {renderErrorMessageFor('object')}
        <Spacer height={2} />
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsRunVerificationGenericV1;
